import type { Experiment } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_pb';
import type { FilterRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/listing_pb';
import type { PropertyConstraint } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/query_pb';
import { Operator } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/query_pb';
import type { RegisteredModel } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_pb';
import type { PlainMessage } from '@bufbuild/protobuf';
import {
  CollapseAllVisibility,
  Dropdown,
  IBreadcrumbItem,
  type IColumn,
  type IContextualMenuItem,
  type IDetailsGroupDividerProps,
  type IGroup,
  MessageBarType,
} from '@fluentui/react';
import {
  Button,
  buttonStylesLink,
  getGroupHeight,
  type IListCellProps,
  type IListCellStyles,
  ListRow,
  ListRowType,
  useHaicPageTitle,
  useToast,
} from '@h2oai/ui-kit';
import React from 'react';
import { useHistory } from 'react-router-dom';
import semver from 'semver';

import { useQueryMyWorkspaces } from '../../authz/authz.hooks';
import { Group } from '../../authz/gen/ai/h2o/user/v1/group_pb';
import { type User, userName } from '../../authz/gen/ai/h2o/user/v1/user_pb';
import { Workspace, workspaceName } from '../../authz/gen/ai/h2o/workspace/v1/workspace_pb';
import { useUsers } from '../../authz/providers/UsersProvider';
import { DateShort } from '../../components/Date/DateShort';
import { FailedToLoadView } from '../../components/FailedToLoadView/FailedToLoadView';
import { HeaderPure } from '../../components/Header/Header';
import { NoItemView } from '../../components/NoItemView/NoItemView';
import {
  currentUserWorkspaceActions,
  PermissionsResolver,
} from '../../components/PermissionsResolver/PermissionsResolver';
import { RowHeaderTitle } from '../../components/RowHeaderTitle/RowHeaderTitle';
import SelectableItemsCallout, {
  SelectableMenuItem,
  SelectedCountBadge,
  toMenuItem,
} from '../../components/SelectableItemCallout/SelectableItemsCallout';
import WidgetList, { WidgetListProps } from '../../components/WidgetList/WidgetList';
import { FALLBACK_MLOPS_API_VERSION, useQueryMlopsApiVersion } from '../../discovery-service/discoveryService.hooks';
import { DeploymentFixed } from '../../mlops/api';
import { DeploymentComposition } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/deployment_composition_pb';
import { Security } from '../../mlops/gen/ai/h2o/mlops/deployer/v2/security_pb';
import { useQueryDeployments } from '../../mlops/mlops.deployer.hooks';
import {
  getPropertyConstraint,
  ListRegisteredModelsSupportedFilterOrderKeys,
} from '../../mlops/mlops.storage.functions';
import {
  RegisteredModelVersionWithExperiment,
  useMutationDeleteExperiments,
  useMutationDeleteModelVersion,
  useMutationDeleteRegisteredModelAndExperiments,
  useQueriesRegisteredModelsVersionsWithExperiments,
  useQueryRegisteredModels,
  useQueryTags,
} from '../../mlops/mlops.storage.hooks';
import { useInfiniteQueryFetchAllPages } from '../../services/hooks';
import { CollapsedContext, useCollapsed } from '../../utils/contexts';
import { useEnv } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import { ContextMenuIconButton } from '../Orchestrator/Workflows';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { ROUTES } from './constants';
import { MLOPS_NO_ITEMS_BANNER_WIDTH } from './Deployments';
import ModelTaggingWidget from './ModelTaggingWidget';
import { ModelTagProvider } from './ModelTagProvider';
import ModelTagView from './ModelTagView';
import NavigationWrapper from './NavigationWrapper';
import PageWrapper from './PageWrapper';
import {
  GranularMLOpsPermissionsResolver,
  isAbleToViewModelTags,
  MLOpsUserActionType,
  useGranularMLOpsPermissions,
} from './userActions';
import { WidgetListFilter } from './WidgetListFilter';

export type DeploymentOptions = {
  // Related to the first model.
  firstModelDeploymentComposition: DeploymentComposition;
  // Related to the second model.
  secondModelDeploymentComposition: DeploymentComposition;
  // General deployment options.
  displayName: string;
  description: string;
  deploymentEnvironmentId: string;
  monitoringEnabled: boolean;
  driftMonitoringEnabled: boolean;
  advanced: {
    toleration: string;
    affinity: string;
  };
  requests: Record<string, string>;
  limits: Record<string, string>;
  kubernetesOptions: {
    replicas: number;
  };
  security?: Security;
  envVariables?: Record<string, string>;
};

export type RegisteredModelItem = RegisteredModel & {
  createdByName: string;
  workspaceNameAndInfo?: string;
};

export type RegisteredModelVersionItem = RegisteredModelVersionWithExperiment & {
  displayName: string;
  description: string;
  createdByName: string;
  contents?: JSX.Element;
  hasLeftOpenClose?: boolean;
  collapsed?: boolean;
  workspaceId: string;
};

export type HistoryState = {
  from: string;
};

export const getDeploymentExperimentsIds = (deployment: DeploymentFixed) => {
  if (deployment?.single) {
    const idModel = deployment.single?.deploymentComposition?.experimentId;
    return [idModel];
  }
  if (deployment?.split) {
    const idModelA = deployment.split?.splitElements[0].deploymentComposition?.experimentId;
    const idModelB = deployment.split?.splitElements[1].deploymentComposition?.experimentId;
    return [idModelA, idModelB];
  }
  if (deployment?.shadow) {
    const idModelA = deployment.shadow.primaryElement?.deploymentComposition?.experimentId;
    const idModelB = deployment.shadow.secondaryElement?.deploymentComposition?.experimentId;
    return [idModelA, idModelB];
  }
  return undefined;
};

export const ListItemDescription = ({
  createdBy,
  createdAt,
  modifiedAt,
}: {
  createdBy: string;
  createdAt: string;
  modifiedAt?: string;
}) => {
  return (
    <span>
      Created on {createdAt ? <DateShort date={createdAt} /> : `unknown`} by <span>{createdBy}</span>
      {modifiedAt && modifiedAt !== createdAt && (
        <>
          {' ('}
          Modified at <DateShort date={modifiedAt} />
          {')'}
        </>
      )}
    </span>
  );
};

export type DeploymentDropdownProps = {
  onGoToDeploymentDetail: (workspaceId: string, deploymentId: string) => void;
  workspaceId: string;
  experimentId: string;
};

export const DeploymentsDropdown = (props: DeploymentDropdownProps) => {
  const { workspaceId, experimentId } = props;
  const permissions = useGranularMLOpsPermissions(workspaceName.compile({ workspace: workspaceId }));
  const { isAbleToViewDeployment, isAbleToUpdateDeployments } = permissions;

  const mlopsApiVersionQuery = useQueryMlopsApiVersion();
  const mlopsApiVersion = mlopsApiVersionQuery.data || FALLBACK_MLOPS_API_VERSION;
  const isMlopsApiV1_0_x = semver.satisfies(mlopsApiVersion, '1.0.x');

  const deploymentsQuery = useQueryDeployments(
    { workspaceId, filter: isMlopsApiV1_0_x ? undefined : `experiment_ids:"${experimentId}"` },
    {
      select: (data) => {
        const deploymentsFromAllPages = data.pages.flatMap((page) => page.deployments || []);
        return { pages: deploymentsFromAllPages, pageParams: data.pageParams };
      },
    }
  );
  useInfiniteQueryFetchAllPages(deploymentsQuery);
  const allDeployments = deploymentsQuery.data?.pages;

  const deploymentsByExperimentId = React.useMemo<Record<string, DeploymentFixed[]>>(() => {
    if (!isMlopsApiV1_0_x) return {};
    const deploymentsMap: Record<string, DeploymentFixed[]> = {};
    allDeployments?.forEach((deployment) => {
      const experimentsIds = getDeploymentExperimentsIds(deployment);
      if (!experimentsIds) return;
      experimentsIds.forEach((experimentId) => {
        if (!deploymentsMap[experimentId]) {
          deploymentsMap[experimentId] = [];
        }
        deploymentsMap[experimentId].push(deployment);
      });
    });
    return deploymentsMap;
  }, [allDeployments]);

  const modelDeployments = isMlopsApiV1_0_x ? deploymentsByExperimentId?.[experimentId] : allDeployments;

  return modelDeployments?.length ? (
    <div>
      <Dropdown
        placeholder={`${modelDeployments?.length} ${modelDeployments?.length === 1 ? 'deployment' : 'deployments'}`}
        options={modelDeployments.map((deployment) => ({
          key: deployment.name || '',
          text: deployment.displayName || deployment.name || '(Unknown deployment)',
        }))}
        onRenderItem={(item) => {
          if (!item) return null;
          return isAbleToViewDeployment || isAbleToUpdateDeployments ? (
            <Button
              menuIconProps={{
                iconName: 'OpenInNewTab',
                // TODO: Theme color.
                style: { fontSize: 12, color: 'black' },
              }}
              text={item?.text}
              onClick={() => {
                const deploymentName = item.key as string;
                const deploymentId = deploymentName.split(`/`).at(-1);
                props.onGoToDeploymentDetail(workspaceId, deploymentId!);
              }}
              styles={[
                buttonStylesLink,
                {
                  textContainer: {
                    color: 'black',
                    fontSize: 12,
                  },
                  flexContainer: {
                    selectors: {
                      '&:hover span': { color: 'var(--h2o-blue500)' },
                      '&:hover i': { color: 'var(--h2o-blue500) !important' },
                    },
                  },
                  root: { paddingLeft: 8, paddingRight: 8 },
                },
              ]}
            />
          ) : null;
        }}
        calloutProps={{
          calloutMaxWidth: 220,
          calloutMinWidth: 200,
          styles: {
            root: {
              padding: '8px 0px',
            },
          },
        }}
        styles={{
          root: { maxWidth: 220 },
          title: {
            border: 'none',
            padding: '0px 35px 0px 0px',
            fontSize: 12,
          },
          caretDownWrapper: {
            i: {
              fontSize: 12,
            },
          },
        }}
      />
    </div>
  ) : (
    <>No deployments</>
  );
};

export const useColumns = ({
  onGoToDeploymentDetail,
  onGoToModelDetail,
  onRenderHeaderButtons,
  onRenderItemButtons,
  hideModelIcon,
  showWorkspaceColumn,
}: {
  onGoToDeploymentDetail: (workspaceId: string, deploymentId: string) => void;
  onGoToModelDetail: (item: RegisteredModelItem) => void;
  onRenderHeaderButtons: (item: RegisteredModelItem) => React.ReactElement;
  onRenderItemButtons: (versionItem: RegisteredModelVersionItem) => React.ReactElement;
  hideModelIcon?: boolean;
  showWorkspaceColumn?: boolean;
}): { headerColumns: IColumn[]; itemColumns: IColumn[] } => {
  const buttonCellStyles: Partial<IListCellStyles> = {
    root: {
      display: 'flex',
      flexGrow: 1,
      justifyContent: 'end',
    },
    text: {
      display: 'flex',
      alignItems: 'center',
      gap: `1rem`,
    },
  };
  const headerColumns: Array<
    IColumn & { data?: { headerFieldName?: string; listCellProps: IListCellProps<RegisteredModelItem> } }
  > = [
    {
      // Empty item to add space for collapse/expand button.
      key: 'toggleSpacer',
      name: '',
      minWidth: 30,
      maxWidth: 30,
    },
    {
      key: 'title',
      name: 'Title',
      fieldName: 'description',
      minWidth: 300,
      maxWidth: 300,
      data: {
        headerFieldName: 'displayName',
        listCellProps: {
          onRenderHeader: (item: RegisteredModelItem) =>
            RowHeaderTitle({
              title: item.displayName,
              onClick: () => onGoToModelDetail(item),
            }),
          onRenderText: ({ createdByName, createdTime }: RegisteredModelItem) => {
            const createdTimeIso = (createdTime?.toJson() as string) || ``;
            return <ListItemDescription createdBy={createdByName} createdAt={createdTimeIso} />;
          },
          ...(!hideModelIcon && {
            iconProps: {
              iconName: 'HomeGroup',
            },
          }),
        },
      },
    },
    {
      key: 'versions',
      name: '',
      fieldName: 'versions',
      minWidth: 100,
      maxWidth: 100,
    },
    {
      key: 'tags',
      name: 'Tags',
      minWidth: 150,
      maxWidth: 220,
      data: {
        listCellProps: {
          // TODO: Check if Tags title is visible.
          onRenderHeader: (item: RegisteredModelItem) => {
            const workspaceFQDN = workspaceName.compile({ workspace: item.projectId });
            return (
              <PermissionsResolver actions={currentUserWorkspaceActions(workspaceFQDN, isAbleToViewModelTags)}>
                {({ allAllowed }) => {
                  return allAllowed ? null : <> </>;
                }}
              </PermissionsResolver>
            );
          },
          onRenderText: (item: RegisteredModelItem) => {
            const workspaceFQDN = workspaceName.compile({ workspace: item.projectId });
            return (
              <PermissionsResolver actions={currentUserWorkspaceActions(workspaceFQDN, isAbleToViewModelTags)}>
                {({ allAllowed }) => {
                  return allAllowed && <ModelTagView registeredModelId={item.id} modelName={item.displayName} />;
                }}
              </PermissionsResolver>
            );
          },
        },
      },
    },
    ...(showWorkspaceColumn
      ? [
          {
            key: 'workspace',
            name: 'Workspace',
            minWidth: 240,
            maxWidth: 280,
            data: {
              listCellProps: {
                onRenderHeader: () => <>Workspace</>,
                onRenderText: ({ workspaceNameAndInfo }: RegisteredModelItem) => <div>{workspaceNameAndInfo}</div>,
              },
            },
          },
        ]
      : []),
    {
      key: 'buttons',
      name: '',
      minWidth: 200,
      data: {
        listCellProps: {
          emptyMessage: '',
          onRenderText: onRenderHeaderButtons,
          styles: buttonCellStyles,
        },
      },
    },
  ];
  const iconWidth = 56; // px
  const firstColumnWidth = 30 + (hideModelIcon ? 0 : iconWidth); // 30px for collapse/expand button
  const versionsWidth = 344 + (hideModelIcon ? iconWidth : 0); // 344px is base versions column
  const itemColumns: Array<IColumn & { data?: { listCellProps: IListCellProps<RegisteredModelVersionItem> } }> = [
    {
      key: 'spacer',
      name: '',
      minWidth: firstColumnWidth,
      maxWidth: firstColumnWidth,
    },
    {
      key: 'version',
      name: 'Version',
      fieldName: 'version',
      minWidth: versionsWidth,
      maxWidth: versionsWidth,
      data: {
        headerFieldName: 'displayName',
        listCellProps: {
          onRenderHeader: ({ version }: RegisteredModelVersionItem) => (
            <h4 style={{ marginRight: 0, marginTop: 0, marginBottom: 0, paddingBottom: 0 }}>{`Version ${version}`}</h4>
          ),
          onRenderText: ({ createdByName, createdTime }: RegisteredModelVersionItem) => {
            const createdTimeIso = (createdTime?.toJson() as string) || ``;
            return <ListItemDescription createdBy={createdByName} createdAt={createdTimeIso} />;
          },
        },
      },
    },
    {
      key: 'tags',
      name: '',
      minWidth: 150,
      maxWidth: 220,
      data: {
        listCellProps: {
          onRenderHeader: () => <></>,
          onRenderText: ({ id, workspaceId }: RegisteredModelVersionItem) => {
            const workspaceFQDN = workspaceName.compile({ workspace: workspaceId });
            return (
              <GranularMLOpsPermissionsResolver workspaceFQDN={workspaceFQDN}>
                {(permissions) => {
                  return permissions?.isAbleToViewModelTags ? (
                    <ModelTaggingWidget modelVersionId={id} isInTheModelList permissions={permissions} />
                  ) : null;
                }}
              </GranularMLOpsPermissionsResolver>
            );
          },
        },
      },
    },
    {
      key: 'deployments',
      name: '',
      minWidth: 200,
      maxWidth: 200,
      data: {
        listCellProps: {
          onRenderHeader: () => <></>,
          onRenderText: ({ experimentId, workspaceId }: RegisteredModelVersionItem) => {
            const workspaceFQDN = workspaceName.compile({ workspace: workspaceId });
            return (
              <PermissionsResolver
                actions={currentUserWorkspaceActions(workspaceFQDN, [MLOpsUserActionType.MLOPS_DEPLOYMENTS_LIST])}
              >
                {({ results: [isAbleToViewDeployments] }) => {
                  return isAbleToViewDeployments ? (
                    <DeploymentsDropdown
                      onGoToDeploymentDetail={onGoToDeploymentDetail}
                      workspaceId={workspaceId}
                      experimentId={experimentId}
                    />
                  ) : null;
                }}
              </PermissionsResolver>
            );
          },
        },
      },
    },
    {
      key: 'buttons',
      name: '',
      minWidth: 200,
      data: {
        listCellProps: {
          emptyMessage: '',
          onRenderText: onRenderItemButtons,
          styles: buttonCellStyles,
        },
      },
    },
  ];
  return { headerColumns, itemColumns };
};

type ModelFiltersState =
  | {
      tags?: string[];
      search?: string;
      owners?: string[];
    }
  | undefined;

type ModelFilterActions =
  | { type: `SET_TAGS`; payload: string[] }
  | { type: `SET_SEARCH`; payload: string }
  | { type: `SET_OWNERS`; payload: string[] }
  | { type: `RESET` };

/**
 * A reducer for ModelFiltersState, the purpose is to hide complexity of the state management. When no filter is set,
 * the state is undefined, so you can simply test for filter set by `Boolean(filterState)`, if truthy (object) some
 * filter is applied, otherwise (undefined) no filter is applied.
 *
 * The reducer maintains the truncation of the state.
 * @param state
 * @param action
 * @returns
 */
const modelsFilterReducer: React.Reducer<ModelFiltersState, ModelFilterActions> = (state, action) => {
  switch (action.type) {
    case `SET_TAGS`:
      if (action.payload.length > 0) {
        return { ...state, tags: action.payload };
      }
      if (!state) return state;
      state.tags = undefined;
      break;
    case `SET_SEARCH`:
      if (action.payload) {
        return { ...state, search: action.payload };
      }
      if (!state) return state;
      state.search = undefined;
      break;
    case `SET_OWNERS`:
      if (action.payload.length > 0) {
        return { ...state, owners: action.payload };
      }
      if (!state) return state;
      state.owners = undefined;
      break;
    case `RESET`:
      return undefined;
    default:
      // @ts-expect-error exhaustive switch-case check
      throw new Error(`Unknown action type: ${action.type}`);
  }
  return Object.values(state).some(Boolean) ? state : undefined;
};

const getFilterRequest = (filter: Exclude<ModelFiltersState, undefined>): PlainMessage<FilterRequest> | undefined => {
  const propertyConstraint: PlainMessage<PropertyConstraint>[] = [];
  if (filter.search) {
    propertyConstraint.push(
      getPropertyConstraint<ListRegisteredModelsSupportedFilterOrderKeys>(`name`, Operator.ICONTAINS, {
        case: `stringValue`,
        value: filter.search,
      })
    );
  }

  return !propertyConstraint.length ? undefined : { query: { clause: [{ propertyConstraint, tagConstraint: [] }] } };
};

const filterByTags = <T extends { tag?: Array<{ id: string }>; experiment?: Experiment }>(
  tags: string[],
  items: T[]
): T[] => {
  const filterTags = new Set(tags);
  return items.filter((item) => {
    const itemTags = item.tag || item.experiment?.tag || [];
    if (filterTags.has(`untagged`) && itemTags.length === 0) return true;
    return itemTags.some((tag) => filterTags.has(tag.id));
  });
};

export const useGroupsAndItems = ({
  clientFilterModelVersions,
  showEmptyGroup,
  registeredModelVersionsWithExperimentsByRegisteredModelId,
  registeredModels,
  usersAndGroupsNameMap,
  workspaces,
}: {
  clientFilterModelVersions?: (
    modelVersions: RegisteredModelVersionWithExperiment[]
  ) => RegisteredModelVersionWithExperiment[];
  registeredModelVersionsWithExperimentsByRegisteredModelId: Record<string, RegisteredModelVersionWithExperiment[]>;
  registeredModels: undefined | RegisteredModel[];
  showEmptyGroup: (
    registeredModel: RegisteredModel,
    filteredVersions: RegisteredModelVersionWithExperiment[]
  ) => boolean;
  usersAndGroupsNameMap?: { [key: string]: User | Group };
  workspaces?: Workspace[];
}): { collapsed: CollapsedContext; groups: IGroup[]; items: RegisteredModelVersionItem[] } => {
  const collapsed = useCollapsed(true);
  if (!registeredModels) return { collapsed, items: [], groups: [] };

  const items: RegisteredModelVersionItem[] = [];
  const groups: IGroup[] = [];
  let versionsWalked = 0;
  for (const registeredModel of registeredModels) {
    const { createdBy, id: registeredModelId } = registeredModel;
    const versions = registeredModelVersionsWithExperimentsByRegisteredModelId[registeredModelId] || [];
    const filteredVersions = clientFilterModelVersions?.(versions) || versions;
    if (!showEmptyGroup(registeredModel, filteredVersions)) continue;

    const registeredModelItem: RegisteredModelItem = {
      ...registeredModel,
      createdByName: usersAndGroupsNameMap?.[`users/${createdBy}`]?.displayName || createdBy,
    } as RegisteredModelItem;
    let workspaceNameAndInfo;
    const workspaceFQDN = workspaceName.compile({ workspace: registeredModel.projectId });
    const workspace = workspaces?.find((ws) => ws.name === workspaceFQDN);
    if (workspace) {
      const personalWorkspaceGroupOrUser =
        workspace.personalWorkspaceSubject && usersAndGroupsNameMap?.[workspace.personalWorkspaceSubject];
      const personalWorkspaceText = !workspace.personalWorkspaceSubject
        ? undefined
        : !personalWorkspaceGroupOrUser
        ? workspace.personalWorkspaceSubject
        : personalWorkspaceGroupOrUser.displayName ||
          (personalWorkspaceGroupOrUser as User).loginPrincipal ||
          personalWorkspaceGroupOrUser.name;
      const text = workspace.displayName! || workspace.name;
      const secondaryText = personalWorkspaceText;
      workspaceNameAndInfo = `${text} ${secondaryText ? ` (${secondaryText})` : ''}`;
    }
    const versionsCount = filteredVersions.length;
    const registeredModelGroup: IGroup = {
      count: versionsCount,
      data: {
        data: {
          ...registeredModelItem,
          versions: versionsCount === 1 ? `1 version` : `${versionsCount} versions`,
          workspaceNameAndInfo,
        },
        hasGroupHeaderData: true,
        index: registeredModelId,
        isLoaded: false,
      },
      isCollapsed: collapsed.isCollapsed(registeredModelId),
      key: registeredModelId,
      name: '',
      startIndex: versionsWalked,
    };
    versionsWalked += versionsCount;
    groups.push(registeredModelGroup);
    items.push(
      ...filteredVersions.map((modelVersion) => {
        const { createdBy } = modelVersion;
        const createByName = userName.compile({ user: createdBy });
        const versionItem: RegisteredModelVersionItem = {
          ...modelVersion,
          createdByName: usersAndGroupsNameMap?.[createByName]?.displayName || createdBy,
          description: registeredModel.description || '',
          displayName: registeredModel.displayName || '',
          workspaceId: registeredModel.projectId || '',
        } as RegisteredModelVersionItem;
        return versionItem;
      })
    );
  }

  return { collapsed, groups, items };
};

const Models = () => {
  const env = useEnv(),
    history = useHistory(),
    { ACTIVE_WORKSPACE_NAME, ACTIVE_WORKSPACE_ID } = useWorkspaces(),
    permissions = useGranularMLOpsPermissions(ACTIVE_WORKSPACE_NAME),
    [modelsFilter, dispatchModelsFilter] = React.useReducer(modelsFilterReducer, undefined),
    breadcrumbs: IBreadcrumbItem[] = [
      {
        key: `home`,
        text: `home`,
        onClick: () => history.push('/'),
      },
      {
        key: `mlops`,
        text: `MLOps`,
        onClick: () => history.push('/mlops'),
      },
      {
        key: 'models',
        text: 'Models',
        isCurrentItem: true,
      },
    ],
    registeredModelsQuery = useQueryRegisteredModels(
      {
        projectId: ACTIVE_WORKSPACE_ID || '',
        paging: {
          pageSize: 20,
          pageToken: undefined as any, // pageToken is wrongly required in the generated proto,
        },
        filter: modelsFilter && getFilterRequest(modelsFilter),
      },
      {
        enabled: Boolean(ACTIVE_WORKSPACE_NAME && permissions?.isAbleToListModels),
        meta: {
          toastError: (err: unknown) => {
            return `Failed to fetch registered models: ${formatError(err)}`;
          },
        },
        select: (data) => {
          const registeredModelsFromAllPages = data.pages.flatMap((page) => page.registeredModels || []);
          return { pages: registeredModelsFromAllPages, pageParams: data.pageParams };
        },
      }
    ),
    registeredModels = registeredModelsQuery.data?.pages,
    { loading: versionsWithExperimentsLoading, registeredModelVersionsWithExperimentsByRegisteredModelId } =
      useQueriesRegisteredModelsVersionsWithExperiments(registeredModels || [], {
        select: (data) => {
          // Sort the model versions by createdTime ASC (oldest first).
          data.modelVersions.sort((a, b) =>
            Number(BigInt(a.createdTime?.seconds || 0) - BigInt(b.createdTime?.seconds || 0))
          );
          return data;
        },
        enabled: permissions?.isAbleToListModels,
      });

  const workspaceQuery = useQueryMyWorkspaces(
    {},
    {
      select: (data) => {
        const flattenedWorkspaces = data.pages.flatMap((page) => page.workspaces || []);
        return { pages: flattenedWorkspaces, pageParams: data.pageParams };
      },
    }
  );
  useInfiniteQueryFetchAllPages(workspaceQuery);
  const workspaces = workspaceQuery.data?.pages;

  const { usersAndGroupsNameMap, usersNameMap } = useUsers();

  const filterOwnersSet = React.useMemo(
    () => (modelsFilter?.owners ? new Set(modelsFilter.owners) : undefined),
    [modelsFilter?.owners]
  );
  const { collapsed, items, groups } = useGroupsAndItems({
    clientFilterModelVersions: (versions) => {
      const versionsFilteredByTags = !modelsFilter?.tags ? versions : filterByTags(modelsFilter.tags, versions);
      const versionsFilteredByOwners = !filterOwnersSet
        ? versionsFilteredByTags
        : versionsFilteredByTags.filter((version) => filterOwnersSet.has(version.createdBy));
      return versionsFilteredByOwners;
    },
    registeredModels,
    registeredModelVersionsWithExperimentsByRegisteredModelId,
    showEmptyGroup: (registeredModel, filteredVersions) => {
      if (filteredVersions.length > 0 || !modelsFilter) {
        // show all models with versions or version-less models if no filter is set
        return true;
      }
      // otherwise if filter is set and none of it's versions match, show model if it matches the filter
      const filterMatchModel = Boolean(
        !modelsFilter.tags && // models on its own has no tags, if tag filter is set, it rejects any model
          (!filterOwnersSet || filterOwnersSet.has(registeredModel.createdBy))
      );
      return filterMatchModel;
    },
    usersAndGroupsNameMap,
    workspaces,
  });

  const actionProps = permissions?.isAbleToCreateModel
      ? {
          actionIcon: 'Add',
          actionTitle: 'Add model',
          onActionClick: () => history.push(`/mlops/${ACTIVE_WORKSPACE_NAME}${ROUTES.MODELS}/add-new`),
        }
      : undefined,
    tagsQuery = useQueryTags(
      {
        // TODO: Use ACTIVE_WORKSPACE_NAME when storage is migrated.
        projectId: ACTIVE_WORKSPACE_ID || '',
      },
      { enabled: Boolean(ACTIVE_WORKSPACE_ID && permissions?.isAbleToViewModelTags) }
    ),
    tags = tagsQuery.data?.tag,
    tagItems: IContextualMenuItem[] = React.useMemo(() => {
      if (!tags?.length) return [];
      return tags
        .map(
          (t) =>
            ({
              key: t.id,
              text: t.displayName,
            } as SelectableMenuItem)
        )
        .concat({
          key: `untagged`,
          text: `Untagged`,
          itemProps: {
            styles: {
              label: {
                fontWeight: 600,
              },
            },
          },
        })
        .map(toMenuItem(modelsFilter?.tags, (newTags) => dispatchModelsFilter({ type: `SET_TAGS`, payload: newTags })));
    }, [tags, modelsFilter?.tags]),
    creatorItems = React.useMemo(() => {
      if (!usersNameMap) return [];
      const users = Object.values(usersNameMap);
      if (!users?.length) return [];
      const toMenuItemCreators = toMenuItem(modelsFilter?.owners, (newOwners) =>
        dispatchModelsFilter({ type: `SET_OWNERS`, payload: newOwners })
      );
      return users.map((u) => {
        const userId = userName.parse(u.name!).user;
        const text: string = u.displayName || u.emails?.[0].address || userId;
        return toMenuItemCreators({
          key: userId,
          text,
        } as SelectableMenuItem);
      });
    }, [modelsFilter?.owners, usersNameMap]),
    loading = registeredModelsQuery.isLoading || versionsWithExperimentsLoading;

  const { addToast } = useToast();
  const { showDialog } = useConfirmDialog();
  const deleteModelVersionMutation = useMutationDeleteModelVersion({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to delete model version: ${formatError(err)}`,
      });
    },
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Model version deleted successfully.',
      });
    },
  });
  const deleteExperimentsMutation = useMutationDeleteExperiments({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to delete experiment: ${formatError(err)}`,
      });
    },
  });
  const handleDeleteRegisteredModelVersionWithExperiments = async (
    versionWithExperiment: RegisteredModelVersionWithExperiment
  ) => {
    await deleteModelVersionMutation.mutateAsync({
      projectId: ACTIVE_WORKSPACE_ID || '',
      versionId: versionWithExperiment.id,
    });
    const experiment = versionWithExperiment.experiment;
    if (experiment) {
      await deleteExperimentsMutation.mutateAsync({
        experimentRequest: [{ id: experiment.id, projectId: ACTIVE_WORKSPACE_ID || '' }],
      });
    }
  };
  const deleteModelAndExperimentsMutation = useMutationDeleteRegisteredModelAndExperiments({
    onError: (err) => {
      addToast({
        messageBarType: MessageBarType.error,
        message: `Failed to delete model: ${formatError(err)}`,
      });
    },
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Model deleted successfully.',
      });
    },
  });
  const { headerColumns, itemColumns } = useColumns({
    onGoToDeploymentDetail: (workspaceId, deploymentId) => {
      window.open(`/mlops/workspaces/${workspaceId}${ROUTES.DEPLOYMENTS}/${deploymentId}`, '_blank');
    },
    onGoToModelDetail: (model: RegisteredModelItem) => {
      history.push(`/mlops/${ACTIVE_WORKSPACE_NAME}${ROUTES.MODELS}/${model.id}`);
    },
    onRenderHeaderButtons: ({ id }) =>
      permissions?.isAbleToCreateModelVersion || permissions?.isAbleToDeleteModelVersion ? (
        <ContextMenuIconButton
          items={[
            ...(permissions?.isAbleToCreateModelVersion
              ? [
                  {
                    key: 'uploadVersion',
                    text: 'Add new model version',
                    onClick: () => {
                      history.push(`/mlops/${ACTIVE_WORKSPACE_NAME}${ROUTES.MODELS}/${id}/add-new-version`, {
                        from: 'models',
                      } as HistoryState);
                    },
                    iconProps: {
                      iconName: 'Add',
                      // TODO: Use theme prop for colors.
                      style: { color: 'var(--h2o-gray900)' },
                    },
                  },
                ]
              : []),
            ...(permissions?.isAbleToDeleteModelVersion
              ? [
                  {
                    key: 'delete',
                    text: 'Delete model',
                    onClick: () => {
                      showDialog?.({
                        title: 'Delete model?',
                        description: 'This will delete the model and all its versions. This action cannot be undone.',
                        onConfirm: () => {
                          deleteModelAndExperimentsMutation.mutate({
                            projectId: ACTIVE_WORKSPACE_ID || '',
                            registeredModelId: id,
                          });
                        },
                      });
                    },
                    style: { color: 'var(--h2o-red400)' },
                    iconProps: {
                      iconName: 'Delete',
                      style: { color: 'var(--h2o-red400)' },
                    },
                  },
                ]
              : []),
          ]}
        />
      ) : (
        <></>
      ),
    onRenderItemButtons: (modelVersion) => {
      const { id } = modelVersion;
      // TODO: Use theme prop for colors.
      return permissions?.isAbleToCreateDeployments ||
        permissions?.isAbleToCreateBatchScoringJobs ||
        permissions?.isAbleToDeleteModelVersion ? (
        <ContextMenuIconButton
          items={[
            ...(permissions?.isAbleToCreateDeployments
              ? [
                  {
                    key: 'deploy',
                    text: 'Deploy version',
                    onClick: () => {
                      history.push(`/mlops/${ACTIVE_WORKSPACE_NAME}${ROUTES.DEPLOYMENTS}/create-new/deploy/${id}`);
                    },
                    iconProps: { iconName: 'PublishContent', style: { color: 'var(--h2o-blue500)' } },
                    style: { color: 'var(--h2o-blue500)' },
                  },
                ]
              : []),
            ...(permissions?.isAbleToCreateBatchScoringJobs
              ? [
                  {
                    key: 'batchScore',
                    text: 'Batch score',
                    onClick: () => {
                      history.push(`/mlops/${ACTIVE_WORKSPACE_NAME}${ROUTES.BATCH_SCORING}/start-new/from-model/${id}`);
                    },
                    iconProps: { iconName: 'AssessmentGroup', style: { color: 'var(--h2o-yellow700)' } },
                    style: { color: 'var(--h2o-yellow700)' },
                  },
                ]
              : []),
            ...(permissions?.isAbleToDeleteModelVersion
              ? [
                  {
                    key: 'delete',
                    text: 'Delete version',
                    onClick: () => {
                      showDialog?.({
                        title: 'Delete model version?',
                        description: `This will delete version ${modelVersion.version} of the model. This action cannot be undone.`,
                        onConfirm: () => handleDeleteRegisteredModelVersionWithExperiments(modelVersion),
                      });
                    },
                    style: { color: 'var(--h2o-red400)' },
                    iconProps: {
                      iconName: 'DeleteRows',
                      style: { color: 'var(--h2o-red400)' },
                    },
                  },
                ]
              : []),
          ]}
        />
      ) : (
        <></>
      );
    },
    hideModelIcon: true,
  });

  const widgetListProps: WidgetListProps = {
    columns: itemColumns,
    items,
    loading: !!loading,
    delayLoader: false,
    isLoadingMore: registeredModelsQuery.isFetchingNextPage,
    onLoadMore: registeredModelsQuery.hasNextPage ? registeredModelsQuery.fetchNextPage : undefined,
    isLoadingSearch: loading && Boolean(modelsFilter?.search),
    searchProps: {
      placeholder: 'Search models',
      onSearchChange: (value: string) => {
        dispatchModelsFilter({ type: `SET_SEARCH`, payload: value });
      },
      searchText: modelsFilter?.search || '',
    },
    actionProps,
    customActions:
      !registeredModels?.length && !modelsFilter ? undefined : (
        <WidgetListFilter>
          {permissions?.isAbleToViewModelTags ? (
            <SelectableItemsCallout
              buttonText={
                (
                  <>
                    Tag
                    {modelsFilter?.tags && <SelectedCountBadge>{modelsFilter.tags.length}</SelectedCountBadge>}
                  </>
                ) as any // ReactNode is proven to work, but fluentui narrows it to string only
              }
              menuItems={tagItems}
              searchBoxPlaceholderText="Search tags"
              emptyItemsText="No tags to select from."
              calloutNoItemsText="No tags found"
              calloutNoItemsIconName="SearchIssue"
              isReadOnly={!permissions?.isAbleToChangeModelTags}
              minimal
              openContextMenuIfNoItems
            />
          ) : null}
          <SelectableItemsCallout
            buttonText={
              (
                <>
                  Creators
                  {modelsFilter?.owners && <SelectedCountBadge>{modelsFilter.owners.length}</SelectedCountBadge>}
                </>
              ) as any // ReactNode is proven to work, but fluentui narrows it to string only
            }
            menuItems={creatorItems}
            searchBoxPlaceholderText="Search creators"
            emptyItemsText="No creators to select from."
            calloutNoItemsText="No creators found"
            calloutNoItemsIconName="SearchIssue"
            minimal
            openContextMenuIfNoItems
          />
          {modelsFilter && (
            <Button
              text="Clear filters"
              onClick={() => dispatchModelsFilter({ type: `RESET` })}
              iconName="Cancel"
              styles={{
                root: { width: 'auto', border: 'none' },
                icon: { fontSize: 12 },
              }}
              buttonContainerStyles={{ root: { textAlign: 'center' } }}
            />
          )}
        </WidgetListFilter>
      ),
    forceShowActionBar: Boolean(modelsFilter),
    NoItemsContent: modelsFilter ? (
      <NoItemView
        title="No models matching active filters"
        description="Try different filters or clear all to see all models."
      />
    ) : (
      <NoItemView
        title="No models"
        description="There are no models available in this workspace. Create the first one to begin."
        bannerWidth={MLOPS_NO_ITEMS_BANNER_WIDTH}
        {...actionProps}
      />
    ),
    ErrorContent: (
      <FailedToLoadView
        title="Failed to load models"
        description="Please try again later. If the problem persists, contact our support."
        actionTitle="Retry"
        onActionClick={registeredModelsQuery.refetch}
        actionIcon="Refresh"
      />
    ),
    groupProps: {
      getGroupHeight: (group: IGroup) => getGroupHeight(group, ListRowType.GroupHeader),
      groupProps: {
        collapseAllVisibility: CollapseAllVisibility.hidden,
        onRenderHeader: (props?: IDetailsGroupDividerProps) => {
          if (!props?.group) return null;
          const { group } = props;
          const item = group.data.data;
          return (
            <ListRow
              data={item}
              columns={headerColumns}
              contents={item?.contents}
              hasLeftOpenClose={true}
              collapsed={group.isCollapsed}
              hasOpenClose={group.count > 0}
              onOpen={() => collapsed.toggleCollapsed(group.key)}
              styles={(theme) => ({
                row: {
                  '&:hover': {
                    background: theme.semanticColors?.contentBackground,
                    '.h2o-ListCell-text': {
                      color: theme.semanticColors?.textSecondary,
                    },
                  },
                },
              })}
            />
          );
        },
        showEmptyGroups: true,
      },
      groups,
    },
  };

  useHaicPageTitle('Models | MLOps', env?.cloudInstanceName);

  return (
    <NavigationWrapper>
      <ModelTagProvider>
        <PageWrapper>
          {registeredModels?.length || modelsFilter || loading ? (
            <HeaderPure items={breadcrumbs} pageTitle="Models" />
          ) : null}
          <WidgetList {...widgetListProps} />
        </PageWrapper>
      </ModelTagProvider>
    </NavigationWrapper>
  );
};

export default Models;
